// @greenlight-spec: 59-platform-agent.md#dashboard-ia
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { CheckCircle2, ChevronDown, ChevronRight, MinusCircle, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

export type TestVerdict = 'passed' | 'failed' | 'passed_with_findings';

export interface RouteCheck {
  label: string;
  ok: boolean | null;
  detail: string;
}

export interface TestReport {
  verdict: TestVerdict;
  routes: { name: string; ok: boolean | null; checks: RouteCheck[] }[];
  diagnosis: string;
  fix?: string;
}

export interface TranscriptEvent {
  id: string;
  kind: 'user' | 'assistant' | 'tool' | 'action' | 'system' | 'report';
  at: string;
  text?: string;
  tool?: { name: string; input: string; output: string; ok: boolean };
  action?: { requestId: string };
  report?: TestReport;
}

/** One transcript event, rendered the same in the session view and every embedded pane. */
export function TranscriptRow({
  event,
  streamedChars,
  renderAction,
}: {
  event: TranscriptEvent;
  /** While an assistant message streams in, how much of it has arrived. */
  streamedChars?: number;
  renderAction?: (requestId: string) => React.ReactNode;
}): React.JSX.Element | null {
  if (event.kind === 'system') {
    return (
      <div className="font-mono text-[11.5px] leading-relaxed text-muted-foreground">
        {event.text}
      </div>
    );
  }
  if (event.kind === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-gl-lg bg-muted px-3 py-2 text-[13.5px]">
          {event.text}
        </div>
      </div>
    );
  }
  if (event.kind === 'assistant') {
    const text = event.text ?? '';
    const shown = streamedChars === undefined ? text : text.slice(0, streamedChars);
    return (
      <div className="max-w-[90%] text-[13.5px] leading-relaxed">
        {shown}
        {streamedChars !== undefined && streamedChars < text.length && (
          <span
            className="ml-0.5 inline-block h-3.5 w-1.5 translate-y-0.5 bg-indigo motion-safe:animate-pulse"
            aria-hidden
          />
        )}
      </div>
    );
  }
  if (event.kind === 'action') {
    return event.action && renderAction ? <>{renderAction(event.action.requestId)}</> : null;
  }
  if (event.kind === 'report' && event.report) {
    return <TestReportCard report={event.report} />;
  }
  return event.tool ? <ToolRow tool={event.tool} /> : null;
}

export function ToolRow({
  tool,
}: {
  tool: NonNullable<TranscriptEvent['tool']>;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="rounded-gl-icon border border-border bg-background">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full min-w-0 items-center gap-2 px-3 py-1.5 text-left text-[12.5px]"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="size-3.5 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="size-3.5 shrink-0" aria-hidden />
        )}
        <span
          className={cn('size-1.5 shrink-0 rounded-full', tool.ok ? 'bg-gl-green' : 'bg-gl-red')}
          aria-hidden
        />
        <span className="sr-only">{tool.ok ? 'Succeeded' : 'Failed'}</span>
        <span className="shrink-0 font-mono font-medium">{tool.name}</span>
        <span className="min-w-0 truncate font-mono text-muted-foreground">{tool.input}</span>
      </button>
      {open && (
        <pre className="overflow-x-auto whitespace-pre-wrap border-t border-border px-3 py-2 font-mono text-[12px] leading-relaxed text-muted-foreground">
          {tool.output}
        </pre>
      )}
    </div>
  );
}

const VERDICT_META: Record<TestVerdict, { label: string; dot: string; tint: string }> = {
  passed: { label: 'Passed', dot: 'bg-gl-green', tint: 'border-gl-green/50' },
  failed: { label: 'Failed', dot: 'bg-gl-red', tint: 'border-gl-red/50' },
  passed_with_findings: {
    label: 'Passed with findings',
    dot: 'bg-gl-amber',
    tint: 'border-gl-amber/60',
  },
};

function CheckIcon({ ok }: { ok: boolean | null }) {
  if (ok === null)
    return (
      <MinusCircle className="mt-0.5 size-3.5 shrink-0 text-slate" aria-label="Not applicable" />
    );
  return ok ? (
    <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-gl-green-text" aria-label="Passed" />
  ) : (
    <XCircle className="mt-0.5 size-3.5 shrink-0 text-gl-red-text" aria-label="Failed" />
  );
}

/** The integration tester's report: verdict, per-route evidence, the cross-diagnosis and the fix. */
export function TestReportCard({ report }: { report: TestReport }): React.JSX.Element {
  const v = VERDICT_META[report.verdict];
  return (
    <section className={cn('rounded-gl-lg border bg-card', v.tint)} aria-label="Test report">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <span className="font-mono text-[11px] uppercase tracking-[0.06em]">Report</span>
        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold">
          <span className={cn('size-2 rounded-full', v.dot)} aria-hidden />
          {v.label}
        </span>
      </header>
      <div className="grid gap-px bg-border sm:grid-cols-2">
        {report.routes.map((r) => (
          <div key={r.name} className="bg-card px-4 py-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-[13px] font-semibold">
              <CheckIcon ok={r.ok} />
              {r.name}
            </div>
            <ul className="space-y-1">
              {r.checks.map((c) => (
                <li key={c.label} className="flex items-start gap-1.5 text-[12px]">
                  <CheckIcon ok={c.ok} />
                  <span>
                    <span className="font-medium">{c.label}</span>
                    <span className="text-muted-foreground"> · {c.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="space-y-2 border-t border-border px-4 py-3 text-[12.5px] leading-relaxed">
        <p>
          <span className="font-mono text-[10.5px] uppercase tracking-[0.06em]">Diagnosis </span>
          {report.diagnosis}
        </p>
        {report.fix && (
          <p>
            <span className="font-mono text-[10.5px] uppercase tracking-[0.06em]">Fix </span>
            {report.fix}
          </p>
        )}
      </div>
    </section>
  );
}

/** The agent's current step while a session runs: an indigo pulse and a line of what it is doing. */
export function LiveStepLine({
  text,
  className,
  wrap,
}: {
  text: string;
  className?: string;
  wrap?: boolean;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        'flex min-w-0 gap-2 text-[12.5px]',
        wrap ? 'items-start' : 'items-center',
        className,
      )}
      role="status"
    >
      <AgentPulse className={wrap ? 'mt-[5px]' : undefined} />
      <span className={wrap ? 'min-w-0' : 'min-w-0 truncate'} key={text}>
        {text}
      </span>
    </span>
  );
}

export function AgentPulse({ className }: { className?: string }): React.JSX.Element {
  return (
    <span className={cn('relative inline-flex size-2 shrink-0', className)} aria-hidden>
      <span className="absolute inline-flex size-full rounded-full bg-indigo opacity-60 motion-safe:animate-ping" />
      <span className="relative inline-flex size-2 rounded-full bg-indigo" />
    </span>
  );
}

export interface StreamedTranscript {
  visible: TranscriptEvent[];
  /** Characters of the newest assistant message shown so far; undefined once it is complete. */
  streamedChars: number | undefined;
  /** The event about to arrive, for the "what it is doing" line. */
  next: TranscriptEvent | undefined;
  done: boolean;
}

/**
 * Replays fixture events as if they were arriving over SSE: one event per step,
 * assistant text a few characters at a time. `stopped` freezes the replay.
 */
export function useStreamedTranscript(
  events: readonly TranscriptEvent[],
  {
    start = 0,
    stepMs = 900,
    stopped = false,
  }: { start?: number; stepMs?: number; stopped?: boolean } = {},
): StreamedTranscript {
  const [count, setCount] = React.useState(Math.min(start, events.length));
  const [chars, setChars] = React.useState(Number.POSITIVE_INFINITY);
  const last = events[count - 1];
  const streamingText = last?.kind === 'assistant' && chars < (last.text?.length ?? 0);

  React.useEffect(() => {
    if (stopped) return;
    if (streamingText) {
      const id = window.setTimeout(() => setChars((c) => c + 4), 16);
      return () => window.clearTimeout(id);
    }
    if (count >= events.length) return;
    const id = window.setTimeout(() => {
      setCount((c) => c + 1);
      setChars(0);
    }, stepMs);
    return () => window.clearTimeout(id);
  }, [count, chars, streamingText, events.length, stepMs, stopped]);

  return {
    visible: events.slice(0, count),
    streamedChars: streamingText ? chars : undefined,
    next: streamingText ? undefined : events[count],
    done: count >= events.length && !streamingText,
  };
}

/** What the agent is doing before the next event lands. */
export function stepLabel(next: TranscriptEvent | undefined): string {
  if (!next) return 'Thinking…';
  if (next.kind === 'tool' && next.tool) return `Running ${next.tool.name} · ${next.tool.input}`;
  if (next.kind === 'report') return 'Writing the report…';
  return 'Thinking…';
}

/** Cycles through a running session's steps so a fixture reads as live. */
export function useCycle<T>(items: readonly T[], everyMs = 2800): T | undefined {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    if (items.length < 2) return;
    const id = window.setInterval(() => setI((n) => (n + 1) % items.length), everyMs);
    return () => window.clearInterval(id);
  }, [items.length, everyMs]);
  return items[i % Math.max(items.length, 1)];
}
